import { computed, Injectable, Signal, inject } from '@angular/core';
import { ConfirmationRemoveModalComponent } from '../../../../../shared/components/modals/confirmation-remove-modal/confirmation-remove-modal.component';
import {
  ClearAccountingState,
  SetAccountingProviderStateFromAccountingConnection,
} from '../../../../../store/accounting/accounting.actions';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AccountingApiService } from '../../../../../_services/accounting-service/accounting-api/accounting-api.service';
import { ToastService } from '../../../../../_services/toast-service/toast.service';
import { Store } from '@ngxs/store';
import { AccountingService } from '../accounting/accounting.service';
import { AccountingConnectionStatus, AccountingProviderCompany } from '../../../../../_interfaces/accounting.interface';
import { ModalComponent } from '../../../../../shared/components/modals/modal/modal.component';
import { TransactionExportConfirmationModalComponent } from '../../transactions/transaction-export-confirmation-modal/transaction-export-confirmation-modal.component';
import { TransactionsApiService } from '../../../../../_services/transactions-service/transactions-api.service';
import { TransactionType } from '../../../../../_interfaces/transactions.interface';

@Injectable({
  providedIn: 'root',
})
export class AccountingModalService {
  readonly modalService = inject(NgbModal);
  readonly transactionsApiService = inject(TransactionsApiService);
  readonly accountingApiService = inject(AccountingApiService);
  readonly accountingService = inject(AccountingService);
  readonly toastService = inject(ToastService);
  readonly store = inject(Store);

  protected provider = computed(() => this.accountingService.provider());
  protected connectionComplete: Signal<boolean> = computed(
    () => this.accountingService.connectionStatus() === AccountingConnectionStatus.Complete,
  );

  deleteConnection() {
    const modalRef = this.modalService.open(ConfirmationRemoveModalComponent, {
      centered: true,
      size: 'md',
    });
    modalRef.componentInstance.action = 'disconnect';
    modalRef.componentInstance.subject = `your ${this.provider()} connection`;
    modalRef.componentInstance.confirmationBtnText = `Disconnect`;
    if (this.connectionComplete()) {
      modalRef.componentInstance.removeMessage = `Your transaction data will be retained. You can reconnect at any time.`;
    }
    modalRef.closed.subscribe({
      next: () => {
        return this.accountingApiService.deleteAccountConnection().subscribe({
          next: () => {
            this.store.dispatch(new ClearAccountingState());
            this.store.dispatch(new SetAccountingProviderStateFromAccountingConnection());
            this.toastService.show(`Accounting connection has been disconnected.`, 'iconoir-check-circle');
          },
          error: () => {
            this.toastService.show('Something went wrong. This accounting connection could not be disconnected.');
          },
        });
      },
    });
  }

  showConnectionErrorModal() {
    const modalRef = this.modalService.open(ModalComponent, {
      centered: true,
      container: 'body', // Mount at body level to avoid aria-hidden conflicts with offcanvas
      size: 'md',
    });

    modalRef.componentInstance.title = `Reconnect to ${this.provider()}`;
    modalRef.componentInstance.bodyText = `We couldn't sync your transactions because there is an issue with your ${this.provider()} connection. To get things syncing again, please reconnect your account in the integration settings.`;
    modalRef.componentInstance.buttonTextDismiss = 'Cancel';
    modalRef.componentInstance.buttonTextAction = 'Reconnect';

    modalRef.closed.subscribe({
      next: () => {
        this.accountingService.reconnectConnection(this.provider() as AccountingProviderCompany);
      },
    });
  }

  exportTransactionsModal(paymentType?: TransactionType) {
    const modalRef = this.modalService.open(TransactionExportConfirmationModalComponent, {
      centered: true,
      size: 'md',
    });
    modalRef.componentInstance.paymentType = paymentType;
    modalRef.closed.subscribe({
      next: () => {
        const type = paymentType?.toString();
        this.transactionsApiService.exportTransactions({ query: { type: type } }).subscribe({
          next: (result) => {
            if (result) {
              this.toastService.show(
                'An email with your detailed transactions will be sent to you.',
                'iconoir-check-circle',
              );
            }
          },
        });
      },
    });
  }
}
